'use client'

import { Box, Button, Flex, Stack, Text, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { staggerContainer, staggerItem } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import { useGeneralAiStatus } from '@/hooks/useGeneralAiStatus'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'

import { GenAiIcon } from '@/components/Icons/GenAiIcon'
import { trackEvent, SegmentEvents } from '@nile/mantine-theme/analytics'
import type { CategoryWidgetProps } from '../types'
import type { GeneralAISmData } from '@/lib/hardcoded-data/CategoryPages/types'
import { SkuGetStartedCta } from '../SkuGetStartedCta'
import { parseHtml } from '@/lib/parseHtml'
import { createBulletRenderers } from '@/lib/parseHtmlBulletRenderers'

export default function WidgetGenAi({ categoryName, data: dataProp, primarySku, skuPageVariant }: CategoryWidgetProps) {
    const { isSm } = useBreakpoint()
    const { isLoading: isStatusLoading, navigateToGeneralAi } = useGeneralAiStatus()

    const generalAISm = (dataProp ?? getCategoryPageData(categoryName, 'generalAISm')) as GeneralAISmData | null

    const bookVisitOnly = generalAISm?.bookVisitOnly ?? false
    const title = generalAISm?.title ?? 'Not sure where to start? Let General AI help.'
    const body =
        generalAISm?.body ??
        'Curious whether your symptoms could be hormonal, cyclical, or something else? Chat with General AI to explore possible causes, learn about treatment paths, and prepare for a more productive visit with your clinician.'

    // The book-visit variant doesn't use the AI CTA or the copilot session check,
    // so it must not be gated on the AI status load (otherwise it flashes blank).
    if (!bookVisitOnly && isStatusLoading) {
        return null
    }

    // NOTE: Two large `filter: blur(171.78px)` decorative ellipses previously rendered
    // here. They were removed because they caused expensive GPU paint/composite work
    // on mobile, particularly Chromium-based engines (Chrome Mobile, Chrome WebView,
    // Samsung Internet) and in-app webviews (Meta, Instagram). See PR #2130 for the
    // identical fix applied to the homepage TopBar. The blue-tinted glow was barely
    // perceptible at 0.15/0.25 opacity behind the dark gradient and the runtime cost
    // far outweighed the visual benefit. Approximated below with static
    // radial-gradient layers in the container background — cheap to paint, no kernel
    // pass on the GPU.

    return (
        <Box
            p={isSm ? '48px 24px' : '72px 72px 72px 48px'}
            pos="relative"
            maw={1440}
            w="100%"
            mx="auto"
            style={{
                borderRadius: 24,
                background:
                    'radial-gradient(ellipse at 0% 0%, rgba(121, 103, 255, 0.15) 0%, transparent 45%),' +
                    'radial-gradient(ellipse at 100% 50%, rgba(169, 181, 255, 0.18) 0%, transparent 55%),' +
                    'linear-gradient(99deg, #201D2F 37.95%, #000 95.91%)',
                overflow: 'hidden',
            }}
        >
            <Flex direction={isSm ? 'column' : 'row'} justify="space-between" align="flex-start" gap="xl">
                <Box
                    component={motion.div}
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px 0px' }}
                    style={{ flex: 1, minWidth: 0 }}
                >
                    <Stack
                        gap={24}
                        align={isSm ? 'flex-start' : 'center'}
                        justify={isSm ? 'flex-start' : 'center'}
                        ta={isSm ? 'left' : 'center'}
                        w="100%"
                    >
                        <MotionBox variants={staggerItem}>
                            <Box
                                w={52}
                                h={52}
                                style={{
                                    borderRadius: '50%',
                                    background: 'var(--gm-fg-hightlight-cont)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <GenAiIcon strokeColor="var(--gm-fg-highlight)" />
                            </Box>
                        </MotionBox>
                        <MotionBox variants={staggerItem}>
                            <Title
                                order={2}
                                fz="var(--gm-refresh-title-md)"
                                fw={400}
                                lh="var(--gm-refresh-title-md-lh)"
                                lts={-1.6}
                                c="gray.1"
                            >
                                {title}
                            </Title>
                        </MotionBox>
                        <MotionBox variants={staggerItem}>
                            <Text
                                size="lg"
                                fw={450}
                                lh="130%"
                                c="gray.2"
                                maw={820}
                                style={{ textWrap: 'balance' }}
                                component="div"
                            >
                                {parseHtml(body, createBulletRenderers({ c: 'var(--mantine-color-gray-2)' }))}
                            </Text>
                        </MotionBox>
                        <MotionBox variants={staggerItem}>
                            <Flex direction={isSm ? 'column' : 'row'} gap={16} align={isSm ? 'stretch' : 'center'}>
                                {!bookVisitOnly && (
                                    <Button
                                        variant="scale-hover"
                                        bg="var(--gm-fg-highlight)"
                                        c="gray.9"
                                        px={24}
                                        py={16}
                                        radius={999}
                                        size="md"
                                        fw={550}
                                        lh={1.4}
                                        h="auto"
                                        onClick={() => {
                                            if (skuPageVariant) {
                                                trackEvent(SegmentEvents.SKU_PAGE_CTA_CLICKED, {
                                                    sku_id: primarySku?.id,
                                                    button_type: 'gen_ai',
                                                    variant: skuPageVariant,
                                                })
                                            }
                                            navigateToGeneralAi('homepage_banner')
                                        }}
                                        styles={{
                                            root: {
                                                flexShrink: 0,
                                                minHeight: 48,
                                            },
                                        }}
                                    >
                                        Start a conversation
                                    </Button>
                                )}

                                {primarySku &&
                                    (bookVisitOnly ? (
                                        <SkuGetStartedCta
                                            sku={primarySku}
                                            theme="primary-white"
                                            label="Talk to a clinician"
                                            skuPageVariant={skuPageVariant}
                                        />
                                    ) : (
                                        <SkuGetStartedCta
                                            sku={primarySku}
                                            theme="outline"
                                            label="Book a clinician visit"
                                            skuPageVariant={skuPageVariant}
                                            style={{
                                                border: '1.127px solid rgba(255, 255, 255, 0.20)',
                                                color: 'gray.1',
                                                background: 'transparent',
                                            }}
                                        />
                                    ))}
                            </Flex>
                        </MotionBox>
                    </Stack>
                </Box>
            </Flex>
        </Box>
    )
}
